import React from 'react';
import AlignedPhotoFrame from '../AlignedPhotoFrame';
import { CardShell, BrandFooter, PhotoTag, scaleFor, ORANGE } from '../CardBits';

// Template F — Twin Wipe. Two pairs, each frozen at the viewer's wipe position.
//
// EXPORT SAFETY: the wipe is a hard-width window with overflow:hidden, NOT
// clip-path — html2canvas@1.4.1 ignores clip-path and would paint each whole
// before-photo over its after-photo. See lib/photoRender.js.
// `form` is deliberately not read: two 9:16 composites side by side fit every
// one of the four ratios without reflowing (at 16:9 they are 607px each inside
// 1920px), so there is no per-form branch to make. Leaving the prop out of the
// signature keeps CI=true from failing the build on an unused binding.
export default function TwinWipeCard({ model, W, H }) {
  const s = scaleFor(H);
  const gap = 6 * s;
  const scrimH = 380 * s;
  const compH = H;
  const maxCompW = (W - gap) / 2;
  const compW = Math.min(maxCompW, compH * 9 / 16);
  const totalW = compW * 2 + gap;
  const originLeft = (W - totalW) / 2;
  const wipeX = compW * Math.min(100, Math.max(0, model.sliderPos)) / 100;

  const pairs = (model.pairs || [{ before: model.before, after: model.after, poseLabel: model.poseLabel }]).slice(0, 2);

  const frame = (side) => (
    <AlignedPhotoFrame
      url={side.url}
      backdropUrl={side.backdropUrl}
      canonical={side.canonical}
      manual={side.manual}
      natural={side.natural}
      slotW={compW}
      slotH={compH}
    />
  );

  return (
    <CardShell W={W} H={H} background="#000000">
      {pairs.map((p, i) => (
        <div key={i} style={{ position: 'absolute', left: originLeft + i * (compW + gap), top: 0, width: compW, height: compH }}>
          {frame(p.after)}

          <div style={{ position: 'absolute', left: 0, top: 0, width: wipeX, height: compH, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, width: compW, height: compH }}>
              {frame(p.before)}
            </div>
          </div>

          <div style={{
            position: 'absolute', left: wipeX - 1.5 * s, top: 0,
            width: 3 * s, height: compH, background: 'rgba(255,255,255,0.9)',
          }} />

          <PhotoTag tone="dark" s={s} style={{ left: 16 * s, top: 16 * s }}>
            {model.labels.before}
            {p.before.weightLabel ? ` · ${p.before.weightLabel}` : ''}
          </PhotoTag>
          <PhotoTag tone="accent" s={s} style={{ right: 16 * s, top: 16 * s }}>
            {model.labels.after}
            {p.after.weightLabel ? ` · ${p.after.weightLabel}` : ''}
          </PhotoTag>
          {p.poseLabel && (
            <PhotoTag tone="dark" s={s} style={{ left: 16 * s, bottom: 300 * s }}>
              {p.poseLabel}
            </PhotoTag>
          )}
        </div>
      ))}

      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: scrimH,
        background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.92))',
      }} />
      <div style={{ position: 'absolute', left: 40 * s, right: 40 * s, bottom: 110 * s }}>
        <div style={{ fontSize: 26 * s, letterSpacing: 4 * s, color: ORANGE, fontWeight: 700 }}>
          {model.hero.sub}
        </div>
        <div style={{ fontSize: 116 * s, fontWeight: 800, lineHeight: 1 }}>
          {model.hero.value}
          {model.hero.unit && <span style={{ fontSize: 48 * s, opacity: 0.6 }}>{model.hero.unit}</span>}
        </div>
      </div>
      <div style={{ position: 'absolute', left: 40 * s, right: 40 * s, bottom: 34 * s }}>
        <BrandFooter handleLabel={model.handleLabel} s={s} bordered={false} />
      </div>
    </CardShell>
  );
}
